import { useCallback, useState } from 'react';

import { ThemeButton } from '@ui/components/ThemeButton';

import { ParticipantDto, Tournament } from '@features/shared/api/typings';
import { DEFAULT_MAX_TOURNAMENT_PARTICIPANTS } from '@features/tournament/constants';
import { useTournamentStates } from '@features/tournament/hooks/useTournamentStates';
import { TeamAddForm } from '@features/tournament/ui/components/TeamAddForm';
import { ParticipantsTab } from '@features/tournament/ui/components/TournamentParticipantsEdit/types';

import styles from '../TournamentParticipantsEdit.module.css';

type Props = {
  tab: ParticipantsTab;
  tournament: Tournament;
  participants?: ParticipantDto[];
  onCreateTeam?: (newTeamName: string) => void;
  showModal: () => void;
  disabled?: boolean;
};

export function Footer({
  tab,
  onCreateTeam,
  tournament,
  participants,
  showModal,
  disabled,
}: Props) {
  const [teamFormVisible, setTeamFormVisible] = useState(false);

  const { isEditable } = useTournamentStates(tournament);

  const handleCreateTeam = useCallback(
    async (newTeamName: string) => {
      await onCreateTeam?.(newTeamName);
      setTeamFormVisible(false);
    },
    [onCreateTeam, setTeamFormVisible],
  );

  const totalTeamsCount = tournament.teams?.length || 0;
  const maxTeams = tournament.teamsCountMax || 0;
  const canAddTeam = totalTeamsCount < maxTeams;

  const totalParticipantsCount = participants?.length || 0;
  const maxParticipants = tournament.participantsCountMax || DEFAULT_MAX_TOURNAMENT_PARTICIPANTS;
  const canAddParticipant = totalParticipantsCount < maxParticipants;

  if (!isEditable) return null;

  return (
    <div className={styles.footer}>
      {teamFormVisible && (
        <TeamAddForm
          totalTeamsCount={totalTeamsCount}
          onSubmit={handleCreateTeam}
          onCancel={() => setTeamFormVisible(false)}
          disabled={disabled}
        />
      )}

      {!teamFormVisible && (
        <>
          {onCreateTeam && (
            <ThemeButton
              className={styles.button}
              onClick={() => setTeamFormVisible(true)}
              disabled={!canAddTeam || !isEditable}
            >
              Добавить команду
            </ThemeButton>
          )}
          {tab === 'Participating' && (
            <ThemeButton
              className={styles.button}
              onClick={showModal}
              disabled={!canAddParticipant || !isEditable}
            >
              Добавить участника
            </ThemeButton>
          )}
          {tab === 'Invited' && tournament.entrance !== 'Open' && (
            <ThemeButton className={styles.button} onClick={showModal} disabled={!isEditable}>
              Пригласить участника
            </ThemeButton>
          )}
        </>
      )}
    </div>
  );
}
